import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { styled } from '@material-ui/styles'
import { FC, useRef } from 'react'
import { useForm } from 'react-final-form'
import { ButtonView } from '../../../control/button/button'
import Dialog from '../../../control/dialog/dialog'
import useTranslate from '../../../i18n/useTranslate'
import { researcherInfo, SearchResearcherResponse } from '../../../services/um/types/researcherType'
import useResearcherHook from '../../web/shareLogic/useResearcherHook'
import { useHistory } from 'react-router-dom'

const CustomPaper = styled(Paper)({
    width: '100%',
    padding: 24,
    margin: '16px 0px',
    '&:hover': {
        border: `1px solid #db5f8e`,
    },
})

const HeaderTypo = styled(Typography)({
    fontWeight: 'bold',
})

const SearchResearcherResult: FC<{
    submitSearch: (values: Record<string, any>, resetPage: boolean, lazy?: boolean) => Promise<void>
    searchResult?: SearchResearcherResponse
}> = ({ submitSearch, searchResult }) => {
    const translate = useTranslate()
    const history = useHistory()
    const dialogSuccessRef = useRef<Record<string, Function>>()
    const dialogDeleteRef = useRef<Record<string, Function>>()
    const deleteUserRef = useRef<researcherInfo>()
    const form = useForm()
    const researcherHook = useResearcherHook()

    const bindSuccessRef = (func: Record<string, Function>) => {
        dialogSuccessRef.current = { ...func }
    }

    const bindDeleteRef = (func: Record<string, Function>) => {
        dialogDeleteRef.current = { ...func }
    }

    const openConfirmDelete = (data: researcherInfo) => {
        deleteUserRef.current = data
        dialogDeleteRef.current?.openDialog()
    }

    const handleDelete = () => {
        researcherHook
            .deleteResearcherByID(deleteUserRef.current?.userID)
            .then(() => {
                dialogDeleteRef.current?.closeDialog()
                dialogSuccessRef.current?.openDialog()
            })
            .catch(() => {
                dialogDeleteRef.current?.closeDialog()
            })
    }

    const onEdit = (id: number) => {
        history.push(`/manage-researcher/${id}/edit`)
    }

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Typography variant="h6">
                        {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${searchResult?.total}`])}
                    </Typography>
                </Grid>
            </Grid>
            {searchResult?.result?.map((data: researcherInfo, index) => (
                <CustomPaper variant="outlined" key={`${data?.userID}`}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
                                <Grid item>
                                    <Grid container direction="row" spacing={2}>
                                        <Grid item>
                                            <Grid item xs={12}>
                                                <ButtonView onClick={() => onEdit(data.userID)}>
                                                    {translate.get('BUTTON.EDIT')}
                                                </ButtonView>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Grid item xs={12}>
                                                <ButtonView color="secondary" onClick={() => openConfirmDelete(data)}>
                                                    {translate.get('BUTTON.DELETE')}
                                                </ButtonView>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                &nbsp; &nbsp; &nbsp;
                                <Grid item xs={true}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <HeaderTypo variant="body2">
                                                {translate.get('UM.USERGROUP.FULLNAME')}
                                            </HeaderTypo>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2">
                                                {translate.languageStore.currentLanguage === translate.languageKey.th
                                                    ? data.fullnameTH
                                                    : data.fullnameEN}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={true}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <HeaderTypo variant="body2">
                                                {translate.get('UM.USERGROUP.USERNAME')}
                                            </HeaderTypo>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2">{data.username}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={true}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <HeaderTypo variant="body2">
                                                {translate.get('UM.PROFILE.CONTACT_NUMBER')}
                                            </HeaderTypo>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2">{data.mobilePhone ?? '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CustomPaper>
            ))}
            <Dialog
                onRef={bindSuccessRef}
                type="alert-message"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.DELETE_SUCCESS')}
                onAfterClose={() => submitSearch(form.getState().values, false)}
            />
            <Dialog
                onRef={bindDeleteRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.WARNING')}
                content={translate.get('ALERT.CONTENT.NEED_TO_DELETE')}
                onClickConfirm={handleDelete}
            />
        </>
    )
}

export default SearchResearcherResult
